import type { RemovedPlayersResponse, RestorePlayerResponse } from '@league/shared/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { LINK_CLASS } from '@league/ui';
import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';
import type { LeagueContext } from './LeagueWorkspace';
import { comparePeople } from './people-display';

export function RemovedPlayersPage() {
  const { league, lifecycle } = useOutletContext<LeagueContext>();
  const client = useQueryClient();
  const [notice, setNotice] = useState('');
  const [fullId, setFullId] = useState<string | null>(null);
  const key = ['removed-players', league.id];
  const path = `/api/director/leagues/${league.id}/removed-players`;
  const query = useQuery({ queryKey: key, queryFn: () => apiFetch<RemovedPlayersResponse>(path) });
  const canRestore = query.data?.canRestore === true && lifecycle?.state !== 'completed';
  const restore = useMutation({
    mutationFn: ({enrollmentId, destination = 'enrolled'}: {enrollmentId: string; destination?: 'enrolled' | 'waitlist'}) => apiPost<RestorePlayerResponse>(`${path}/${enrollmentId}/restore`, {destination}),
    onMutate: () => setNotice(''),
    onSuccess: async (result, variables) => {
      if (result.outcome === 'full') { setNotice(result.message); setFullId(variables.enrollmentId); return; }
      setFullId(null);
      setNotice(result.outcome === 'waitlisted' ? 'Player added to the waitlist.' : result.outcome === 'unchanged' ? 'Player is already on the roster or waitlist.' : 'Player added to the roster.');
      await Promise.all([
        key, ['roster', league.id], ['director-leagues'], ['director-dashboard'],
        ['standings', league.id], ['play-together-groups', league.id],
      ].map((queryKey) => client.invalidateQueries({ queryKey })));
    },
    onError: () => { void client.invalidateQueries({ queryKey: key }); },
  });
  const removed = [...(query.data?.players ?? [])].sort((a, b) => comparePeople(a.player, b.player));
  return <section className="flex flex-col gap-4">
    <div>
      <h2 className="font-display text-xl">Removed Players</h2>
      <Link to={`/director/leagues/${league.id}/roster`} className={`mt-2 inline-block text-[13px] ${LINK_CLASS}`}>Back to Roster</Link>
    </div>
    {query.isPending ? <p className="text-sm text-ink-3">Loading…</p> : null}
    {query.isError ? <ErrorNote>Could not load removed players. Please refresh the page.</ErrorNote> : null}
    {query.data && !canRestore ? <p className="text-sm text-ink-2">This completed league’s player history is read-only.</p> : null}
    {notice ? <p role="status" className="rounded-md border border-light bg-paper px-4 py-3 text-sm text-ink-2">{notice}</p> : null}
    {fullId ? <Button variant="secondary" disabled={restore.isPending} onClick={() => restore.mutate({enrollmentId: fullId, destination: 'waitlist'})}>Add to waitlist</Button> : null}
    {restore.error ? <ErrorNote>{restore.error instanceof ApiError ? restore.error.message : 'Could not add this player. Please try again.'}</ErrorNote> : null}
    {query.isSuccess && removed.length === 0 ? <p className="text-sm text-ink-3">No removed players.</p> : null}
    {removed.length > 0 ? <ul className="divide-y divide-light rounded-md border border-light bg-paper">
      {removed.map(({ enrollmentId, player, previouslyEnrolled }) => <li key={enrollmentId} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
        <div>
          <p className="font-medium">{player.firstName} {player.lastName}</p>
          <p className="text-[12px] text-ink-3">{player.email ?? 'No email'}</p>
        </div>
        {canRestore ? <Button variant="secondary" size="inline" disabled={restore.isPending} onClick={() => restore.mutate({enrollmentId})}>
          {restore.isPending && restore.variables?.enrollmentId === enrollmentId ? 'Adding…' : previouslyEnrolled ? 'Return to Roster' : 'Add to Roster'}
        </Button> : null}
      </li>)}
    </ul> : null}
  </section>;
}
